import { Badge, PersonAdd, SwapVert } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";
import { sentAssessmentInvitation } from "api/assessments/sentAssessmentInvitation";
import { sentAssessmentInvitationBulk } from "api/assessments/sentAssessmentInvitationBulk";
import fetchCompanyJob from "api/companies/fetchCompanyJob";
import DataList from "components/DataList";
import {
  APPLICANT_COMPANIES_JOBS_OPTIONS,
  JOB_APPLICANTS_OPTIONS,
  JOB_STATUS_OPTIONS,
} from "components/FilterButtons/options";
import GenericModal from "components/GenericModal";
import MDTypography from "components/MDTypography";
import SplitPanelSelector from "components/SplitPanelSelector";
import { useAppContextController } from "context/AppContext";
import FormField from "layouts/applications/wizard/components/FormField";
import fetchApplicants from "layouts/pages/applicants/actions/fetchApplicants";
import { ConfirmationModal } from "layouts/pages/setup/components/DynamicForm/components";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "react-query";
import { mapObject } from "utils/helpers/arrays";
import AssessmentBulkAssignApplicantsRow from "./AssessmentBulkAssignApplicantsRow";

const sortValues = {
  FIRSTNAME: "firstName:asc",
  RANK: "jobsCopy.score:desc,firstName:asc,_id:asc",
  APPLICATION_DATE: "jobsCopy.dateModified:desc,firstName:asc",
};

const AssessmentBulkAssignApplicantsModal = ({ open, setOpen, assessment, onAdd, jobSlug }) => {
  const { currentLoggedUser, companyType } = useAppContextController();
  const { enqueueSnackbar } = useSnackbar();

  const [applicantDataFilter, setApplicantDataFilter] = useState({
    status: "all",
  });
  const [dataFilter, setDataFilter] = useState({ status: "Applies" });
  const [dataVenueFilter, setDataVenueFilter] = useState({ status: "all" });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(sortValues.RANK);
  const [assessmentSendInvitationSelected, setAssessmentSendInvitationSelected] = useState(null);
  const [inviteAllConfirmationModalOpen, setInviteAllConfirmationModalOpen] = useState(null);

  const [currentJobSlug, setCurrentJobSlug] = useState(jobSlug);

  const [anchorEl, setAnchorEl] = useState(null);
  const handleIconClick = (e) => setAnchorEl(e.currentTarget);
  const handleIconClose = () => setAnchorEl(null);

  const handleSortByChange = (e) => {
    setSortBy(e.target.value);
    handleIconClose();
  };

  const allowedAudienceStatus = useMemo(() => {
    const allowApp = !!assessment?.audience?.find((item) =>
      ["New", "ATC", "Screened", "Pre-Hire", "Declined"].includes(item)
    );
    const allowEmp = assessment?.audience?.includes("Employee");
    const allowB = allowApp && allowEmp;

    let allowedStatus = "";
    if (allowB) allowedStatus = "Applicant;Employee";
    else if (allowApp) allowedStatus = "Applicant";
    else if (allowEmp) allowedStatus = "Employee";
    return allowedStatus;
  }, [assessment?.audience]);

  const allowedAudienceApplicantStatus = useMemo(() => {
    return (assessment?.audience ?? []).filter((item) => item !== "Employee");
  });

  const orderByOverride = sortBy;

  const options = useMemo(() => {
    const result = {
      fetchAll: false,
      orderByOverride,
      limit: 10,
      useElemMatch: true,
    };
    return result;
  }, [orderByOverride]);

  const fetchOptions = useMemo(() => {
    const updatedOptions = {
      ...options,
      filters: {
        // "jobs.jobSlug": jobSlug,
        status: allowedAudienceStatus,
      },
      page,
    };
    if (currentJobSlug) {
      updatedOptions.filters = {
        ...updatedOptions.filters,
        "jobs.jobSlug": currentJobSlug,
      };
      if (companyType === "Company") {
        if (applicantDataFilter.status !== "all")
          updatedOptions.filters = {
            ...updatedOptions.filters,
            "jobs.applicantStatus":
              allowedAudienceStatus === "Employee" || allowedAudienceStatus === "Applicant;Employee"
                ? applicantDataFilter.status
                : (allowedAudienceApplicantStatus ?? []).find(
                    (item) => item === applicantDataFilter.status
                  ) ?? "",
          };
        else if (allowedAudienceStatus !== "Employee")
          updatedOptions.filters = {
            ...updatedOptions.filters,
            "jobs.applicantStatus": (allowedAudienceApplicantStatus ?? []).join(";"),
          };
      } else if (applicantDataFilter.status !== "all")
        updatedOptions.filters = {
          ...updatedOptions.filters,
          applicantStatus:
            allowedAudienceStatus === "Employee" || allowedAudienceStatus === "Applicant;Employee"
              ? applicantDataFilter.status
              : (allowedAudienceApplicantStatus ?? []).find(
                  (item) => item === applicantDataFilter.status
                ) ?? "",
        };
      else if (allowedAudienceStatus !== "Employee")
        updatedOptions.filters = {
          ...updatedOptions.filters,
          applicantStatus: (allowedAudienceApplicantStatus ?? []).join(";"),
        };

      if (companyType === "Company" && dataFilter.status !== "Applies") {
        updatedOptions.filters = {
          ...updatedOptions.filters,
          "jobs.employmentStage": dataFilter.status,
        };
      } else if (companyType !== "Company" && dataVenueFilter.status !== "all") {
        updatedOptions.filters = {
          ...updatedOptions.filters,
          "jobs.status": dataVenueFilter.status,
        };
      }
    }

    return updatedOptions;
  }, [
    options,
    allowedAudienceStatus,
    page,
    currentJobSlug,
    applicantDataFilter.status,
    allowedAudienceApplicantStatus,
    companyType,
    dataFilter.status,
    dataVenueFilter.status,
  ]);

  const mapApplicantsAddExtraInfo = (appl) => {
    let rank = 0;
    let aiRank = null;
    let jobModifiedDate = "";
    let jobApplyDate = "";
    let jobAgent = "";
    let jobModifiedAgent = "";
    let jobModifiedAgentName = "";
    let aiScreened = null;
    let waitingForCall = null;
    if (currentJobSlug) {
      const foundJob = (appl.jobs ?? []).find((applJob) => applJob.jobSlug === currentJobSlug);
      if (foundJob) {
        rank = foundJob.score ?? 0;
        jobModifiedDate = foundJob.dateModified ?? "";
        jobApplyDate = foundJob.applyDate ?? "";
        jobAgent =
          foundJob.agent !== "Internet" && foundJob.agent !== "Indeed" ? foundJob.agent : "";
        jobModifiedAgent = foundJob.modifiedAgent ?? "";
        jobModifiedAgentName = foundJob.modifiedAgentName ?? "";
        aiScreened = foundJob.aiScreened;
        waitingForCall = foundJob.waitingForCall;
        if (foundJob.aiInterview?.score != null) {
          aiRank = foundJob.aiInterview?.score ?? 0;
        }
      }
    }

    return {
      ...appl,
      rank,
      aiRank,
      jobModifiedDate,
      jobApplyDate,
      jobAgent,
      jobModifiedAgent,
      jobModifiedAgentName,
      aiScreened,
      waitingForCall,
    };
  };

  const queryFunction = async ({ queryKey, pageParam }) => {
    let data = await fetchApplicants({ ...fetchOptions, page: pageParam ?? 1 });
    if (data?.data) {
      data = {
        ...data,
        data: data.data.map(mapApplicantsAddExtraInfo),
      };
    }
    return data;
  };

  const {
    data: applicants,
    isLoading,
    isFetching,
    refetch: refetchApplicants,
    fetchNextPage,
  } = useInfiniteQuery(["jobsStatusApplicants", fetchOptions], queryFunction, {
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.data?.length || !lastPage?.pagination?.next?.page) {
        return undefined;
      }

      const nextPage = lastPage?.pagination?.next?.page;
      return nextPage ?? false;
    },
    cacheTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    enabled: !!allowedAudienceStatus && !!currentJobSlug,
  });

  const allOptionsJobs = {
    fetchAll: true,
    filters: {},
  };

  const {
    data: jobs,
    isLoading: isLoadingJobs,
    refetch,
  } = useQuery(["availableJobs", allOptionsJobs], () => fetchCompanyJob(allOptionsJobs));

  const jobNames = useMemo(() => {
    if (jobs?.data) {
      return jobs?.data?.map((job) => ({
        ...job,
        label: `${job.title} - ${job.companyName ?? job.venueName}`,
      }));
    }
    return [];
  }, [jobs?.data]);

  const jobsByJobSlug = useMemo(() => {
    if (!jobNames?.length) return {};
    return mapObject(jobNames, (a) => a.jobSlug);
  }, [jobNames]);

  const fullData = useMemo(
    () =>
      applicants?.pages?.flatMap((p) => {
        const currentItems = p?.data || [];
        return currentItems;
      }),
    [applicants?.pages]
  );

  // const { jobApplicantOptions, jobStatusOptions } = useJobStatusFilterCards({
  //   currentJob: { jobSlug: currentJobSlug },
  //   applicantStatus: applicantDataFilter.status,
  //   isShowingEmployees: true,
  //   noCount: true,
  // });

  const jobApplicantDropdownOptions = useMemo(() => {
    return JOB_APPLICANTS_OPTIONS.map((item) => ({
      title: item.title,
      icon: item.icon,
      hasShadow: applicantDataFilter.status === item.value ? "emd" : "",
      color: item.iconColor,
      ariaLabel: item.title,
      fn: () => setApplicantDataFilter((prevState) => ({ ...prevState, status: item.value })),
      name: item.value,
      selectedTitle: item.value === "all" ? "Applicant Filter" : null,
      selectedIcon: item.value === "all" ? <PersonAdd fontSize="small" color="warning" /> : null,
      badgeCount: 0,
    }));
  }, [applicantDataFilter.status]);

  const jobStatusDropdownOptions = useMemo(() => {
    return JOB_STATUS_OPTIONS.map((item) => ({
      title: item.title,
      icon: item.icon,
      hasShadow: dataFilter.status === item.value ? "emd" : "",
      color: item.iconColor,
      ariaLabel: item.title,
      fn: () => setDataFilter((prevState) => ({ ...prevState, status: item.value })),
      name: item.value,
      selectedTitle: item.value === "Applies" ? "Job Filter" : null,
      selectedIcon: item.value === "Applies" ? <Badge fontSize="small" color="success" /> : null,
      badgeCount: 0,
    }));
  }, [dataFilter.status]);

  const jobStatusVenueDropdownOptions = useMemo(() => {
    return APPLICANT_COMPANIES_JOBS_OPTIONS.map((item) => ({
      title: item.title,
      icon: item.icon,
      hasShadow: dataVenueFilter.status === item.value ? "emd" : "",
      color: item.iconColor,
      ariaLabel: item.title,
      fn: () => setDataVenueFilter((prevState) => ({ ...prevState, status: item.value })),
      name: item.value,
      selectedTitle: item.value === "all" ? "Job Filter" : null,
      selectedIcon: item.value === "all" ? <Badge fontSize="small" color="success" /> : null,
      badgeCount: 0,
    }));
  }, [dataVenueFilter.status]);

  const handleSendInvitation = (asse, app) => {
    setAssessmentSendInvitationSelected({ assessment: asse, applicant: app });
  };

  const handleCloseSendInvitationModal = () => {
    setAssessmentSendInvitationSelected(null);
  };

  const sendAssessmentInviteMutation = useMutation(sentAssessmentInvitation, {
    onError: (err) => {
      console.log("error", err);
      enqueueSnackbar("Something went wrong!", { variant: "error" });
    },
    onSuccess: async (_, { applicantId, jobSlug: jbSlug }) => {
      enqueueSnackbar("Invitation sent!", { variant: "success" });
    },
  });

  const sendAssessmentInviteBulkMutation = useMutation(sentAssessmentInvitationBulk, {
    onError: (err) => {
      console.log("error", err);
      enqueueSnackbar("Something went wrong sending multiple invitations!", { variant: "error" });
    },
    onSuccess: async (_, { applicantId, jobSlug: jbSlug }) => {
      // enqueueSnackbar("Invitations sent!", { variant: "success" });
    },
  });

  const confirmSendInvitation = async () => {
    if (!assessmentSendInvitationSelected) return;
    await sendAssessmentInviteMutation.mutateAsync({
      assessmentId: assessment?._id,
      applicantId: assessmentSendInvitationSelected.applicant?._id,
    });
  };

  const confirmSendInvitationAll = async () => {
    sendAssessmentInviteBulkMutation.mutateAsync({
      assessmentId: assessment?._id,
      ...fetchOptions,
      fetchAll: true,
      limit: null,
    });
    enqueueSnackbar("Invitations sent!", { variant: "success" });
  };

  const header = (
    <>
      <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }}>
        Assessment Available Candidates
      </MDTypography>
      <Grid xs={12} container justifyContent="space-between">
        <MDTypography
          variant="body2"
          color={assessment?.audience?.length ? "info" : "error"}
          sx={{ ml: 2 }}
        >
          {`Allowed Audience: ${
            !assessment?.audience?.length ? "None" : ""
          }${assessment?.audience?.join(", ")}`}
        </MDTypography>
        <Button
          variant="contained"
          style={{ color: "white" }}
          onClick={(e) => {
            setInviteAllConfirmationModalOpen(true);
          }}
        >
          Invite All
        </Button>
      </Grid>
    </>
  );

  const modalBody = (
    <Box>
      <Grid container rowSpacing={2} sx={{ opacity: 1, gap: 2, pb: 4, alignItems: "flex-end" }}>
        <Grid item xs={4}>
          <Autocomplete
            options={jobNames || []}
            freeSolo
            defaultValue=""
            loading={isLoadingJobs}
            value={currentJobSlug ? jobsByJobSlug[currentJobSlug]?.label || "" : ""}
            onChange={(e, v) => {
              setCurrentJobSlug(v?.jobSlug);
            }}
            disabled={!!jobSlug}
            renderOption={(props, option, index) => {
              const key = `listItem-${index}-${option._id}`;
              return (
                <li {...props} key={key}>
                  {option.label}
                </li>
              );
            }}
            sx={{
              width: "100%",
              marginTop: 2,
            }}
            renderInput={(params) => (
              <FormField {...params} type="text" label="Job Name" fullWidth />
            )}
          />
        </Grid>
        <Box sx={{ marginBottom: 1 }}>
          <SplitPanelSelector
            panelOptions={jobApplicantDropdownOptions}
            selectedPanel={applicantDataFilter?.status}
            isEdit
            disabled={!currentJobSlug}
          />
        </Box>
        <Box sx={{ marginBottom: 1 }}>
          {companyType === "Company" ? (
            <SplitPanelSelector
              panelOptions={jobStatusDropdownOptions}
              selectedPanel={dataFilter?.status}
              isEdit
              disabled={!currentJobSlug}
            />
          ) : (
            <SplitPanelSelector
              panelOptions={jobStatusVenueDropdownOptions}
              selectedPanel={dataVenueFilter?.status}
              isEdit
              disabled={!currentJobSlug}
            />
          )}
        </Box>
        <Grid item flexGrow={1} />
        <Button
          variant="text"
          startIcon={<SwapVert />}
          onClick={handleIconClick}
          // disabled={isSubmitting}
          style={{ fontSize: 14, opacity: currentJobSlug ? 1 : 0.5 }}
          disabled={!currentJobSlug}
        >
          Sort By
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleIconClose}
          sx={{
            "& .MuiPaper-root": { minWidth: "10rem", p: "0.2rem" },
            li: { minWidth: "10rem", pl: "0.5rem", pr: "0.5rem" },
          }}
        >
          <RadioGroup
            aria-labelledby="sortbyradiobuttons"
            value={sortBy}
            onChange={handleSortByChange}
            name="radio-buttons-group"
            sx={{ p: "0.2rem" }}
          >
            <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
              <FormControlLabel value={sortValues.FIRSTNAME} control={<Radio />} label="Name" />
            </MenuItem>
            <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
              <FormControlLabel value={sortValues.RANK} control={<Radio />} label="Rank" />
            </MenuItem>
            <MenuItem onClick={() => {}} disableRipple sx={{ p: 0, width: "5rem" }}>
              <FormControlLabel
                value={sortValues.APPLICATION_DATE}
                control={<Radio />}
                label="Last Activity Date"
              />
            </MenuItem>
          </RadioGroup>
        </Menu>
      </Grid>
      <DataList
        renderRow={(row, idx) => (
          <AssessmentBulkAssignApplicantsRow
            row={row}
            idx={idx}
            jobSlug={currentJobSlug}
            assessment={assessment}
            onSendInvitation={handleSendInvitation}
          />
        )}
        data={fullData ?? []}
        greybar
        divider
        scrollY
        tableHeight={450}
        height={500}
        isLoading={isLoading}
        noPadding
        isFetching={isFetching}
        loadMore={fetchNextPage}
        infiniteData={applicants?.pages?.[applicants?.pages?.length - 1]}
      />
    </Box>
  );

  const buttons = (
    <>
      <Box display="flex" flexDirection="column" sx={{ paddingRight: "1rem" }}>
        <Button
          variant="contained"
          style={{ color: "white" }}
          onClick={(e) => {
            setOpen(false);
          }}
        >
          Close
        </Button>
      </Box>
    </>
  );

  return (
    <>
      <GenericModal
        open={open}
        setOpen={setOpen}
        body={modalBody}
        header={header}
        buttons={buttons}
        sx={(theme) => ({
          width: "90% !important",
          maxWidth: "1100px !important",
          // paddingBottom: "10px",
          [theme.breakpoints.down("md")]: {
            "& .MuiCardContent-root": {
              padding: "0px",
            },
          },
        })}
      />
      {!!assessmentSendInvitationSelected && (
        <ConfirmationModal
          open={!!assessmentSendInvitationSelected}
          onClose={handleCloseSendInvitationModal}
          title="Send Invite"
          message={`Are you sure you want to send an invitation to ${assessmentSendInvitationSelected?.applicant?.firstName} ${assessmentSendInvitationSelected?.applicant?.lastName}?`}
          onConfirm={() => {
            confirmSendInvitation();
            handleCloseSendInvitationModal();
          }}
        />
      )}
      {inviteAllConfirmationModalOpen && (
        <ConfirmationModal
          open={inviteAllConfirmationModalOpen}
          onClose={() => setInviteAllConfirmationModalOpen(false)}
          title="Invite All"
          message="This will send an invitation to all the people that match the current filters. Are you sure you want to continue?"
          onConfirm={() => {
            confirmSendInvitationAll();
            setInviteAllConfirmationModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default AssessmentBulkAssignApplicantsModal;
